import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { MapPinIcon, MapPinPlusIcon, UserIcon, SignOutIcon, SlidersHorizontalIcon, CalendarBlankIcon, MountainsIcon, ListIcon, BicycleIcon, UsersThreeIcon } from "@phosphor-icons/react";
import { useDiscoverEvents } from "@/context/DiscoverEventsContext";
import { useAuth } from "@/context/AuthContext";
import { isAdminUser } from "@/lib/admin";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { t } from "@/i18n/messages";
import { useLanguage } from "@/i18n/useLanguage";

export function Navbar({ scrollsWithPage = false, brandVariant = "full" }) {
  useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const discovery = useDiscoverEvents();
  const showWordmark = pathname === "/" && brandVariant !== "mark";
  const effectiveBrandVariant = showWordmark ? "full" : "mark";
  const switchLayer = (eventsVisible) => {
    discovery.setVisible(eventsVisible);
    discovery.setSelectedEventId(null);
  };

  return (
    <header
      className={`${scrollsWithPage ? "absolute md:fixed" : "fixed"} left-0 right-0 top-0 z-[1200] flex h-16 items-center justify-between bg-[#111113]/95 px-3 backdrop-blur-xl md:h-auto md:bg-transparent md:px-6 md:py-3 md:backdrop-blur-none`}
      data-testid="navbar"
    >
      <Link to="/" className={`group flex items-center ${effectiveBrandVariant === "mark" ? "gap-0" : "gap-2.5"}`} data-testid="logo-link" aria-label={t("nav.homeAria")}>
        <img
          src="/images/logo/logo-tr-small.png"
          srcSet="/images/logo/logo-tr-small.png 128w, /images/logo/logo-tr-medium.png 512w, /images/logo/logo-tr-big.png 1536w"
          sizes={effectiveBrandVariant === "mark" ? "48px" : "64px"}
          alt=""
          aria-hidden="true"
          className={`${effectiveBrandVariant === "mark" ? "h-8 w-14" : "h-9 w-16"} object-contain transition-transform group-hover:scale-105`}
        />
        {showWordmark && <div className="hidden leading-none lg:block"><div className="font-display text-xl font-extrabold uppercase tracking-tight text-white">Bikeparks <span className="text-[#CCFF00]">Finder</span></div><div className="label-tiny mt-0.5">{t("brand.tagline")}</div></div>}
      </Link>

      <div className="flex shrink-0 items-center gap-1.5 md:hidden">
        {discovery && (
          <div className="flex items-center gap-0.5 rounded-full border border-white/10 bg-black/60 p-1" role="group" aria-label={t("nav.discoveryLayer")}>
            <button type="button" aria-label={t("nav.spots")} title={t("nav.spots")} aria-pressed={!discovery.visible} data-testid="mobile-spots-layer"
              onClick={() => switchLayer(false)}
              className={`flex h-9 w-9 items-center justify-center rounded-full ${!discovery.visible ? "bg-[#CCFF00] text-black" : "text-zinc-300"}`}>
              <MountainsIcon size={19} weight="bold" />
            </button>
            <button type="button" aria-label={t("nav.events")} title={t("nav.events")} aria-pressed={discovery.visible} data-testid="mobile-events-layer"
              onClick={() => switchLayer(true)}
              className={`flex h-9 w-9 items-center justify-center rounded-full ${discovery.visible ? "bg-violet-400 text-black" : "text-zinc-300"}`}>
              <CalendarBlankIcon size={19} weight="bold" />
            </button>
          </div>
        )}
        <Link to={user ? "/profile" : "/login"} aria-label={user ? t("nav.profile") : t("nav.signIn")} data-testid="mobile-profile"
          className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-black/60 text-[#CCFF00]">
          {user ? <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#CCFF00] text-sm font-bold text-black">{(user.name || user.email || "R")[0].toUpperCase()}</span> : <UserIcon size={21} />}
        </Link>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button type="button" aria-label={t("nav.openMenu")} data-testid="mobile-menu-trigger"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-black/60 text-white">
              <ListIcon size={22} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={8}
            className="z-[1300] max-h-[calc(100dvh-5rem)] w-[min(18rem,calc(100vw-1.5rem))] overflow-y-auto border-white/10 bg-[#1c1c1f] text-white"
          >
            <DropdownMenuItem onSelect={() => navigate("/")} className="cursor-pointer focus:bg-white/10">{t("nav.discover")}</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => navigate("/events")} className="cursor-pointer focus:bg-white/10">{t("nav.events")}</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => navigate("/ride-planner")} className="cursor-pointer focus:bg-white/10">{t("nav.ridePlanner")}</DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => navigate("/add-destination")}
              className={`cursor-pointer focus:bg-white/10 ${pathname === "/add-destination" ? "bg-[#CCFF00]/10 text-[#CCFF00]" : ""}`}
            >
              {t("nav.addDestination")}
            </DropdownMenuItem>
            {user && <DropdownMenuSeparator className="bg-white/10" />}
            {user && isAdminUser(user) && (
              <DropdownMenuItem
                onSelect={() => navigate("/admin")}
                className={`cursor-pointer focus:bg-white/10 ${pathname.startsWith("/admin") ? "bg-[#CCFF00]/10 text-[#CCFF00]" : ""}`}
              >
                {t("nav.adminConsole")}
              </DropdownMenuItem>
            )}
            {user && (
              <DropdownMenuItem
                onSelect={() => navigate("/profile")}
                className={`cursor-pointer focus:bg-white/10 ${pathname === "/profile" ? "bg-[#CCFF00]/10 text-[#CCFF00]" : ""}`}
              >
                {t("nav.riderProfile")}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onSelect={() => navigate("/my-map")}
              className={`cursor-pointer focus:bg-white/10 ${pathname === "/my-map" ? "bg-[#CCFF00]/10 text-[#CCFF00]" : ""}`}
            >
              {t("nav.myMap")}
            </DropdownMenuItem>
            {user && <DropdownMenuItem onSelect={async () => { await logout(); toast.success(t("nav.signOut")); navigate("/"); }} className="cursor-pointer text-[#FF4500] focus:bg-white/10">{t("nav.signOut")}</DropdownMenuItem>}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="hidden items-center gap-2 rounded-full border border-white/10 bg-black/90 p-1.5 md:flex">
        <Link to="/ride-planner" aria-current={pathname === "/ride-planner" ? "page" : undefined} className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold transition-colors hover:bg-white/10 ${pathname === "/ride-planner" ? "bg-[#CCFF00]/15 text-[#CCFF00]" : "text-zinc-200"}`} data-testid="nav-ride-planner">
          <BicycleIcon size={18} className="text-[#CCFF00]" /><span className="hidden lg:inline">{t("nav.planRide")}</span>
        </Link>
        <Link
          to="/add-destination"
          aria-current={pathname === "/add-destination" ? "page" : undefined}
          className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold transition-colors hover:bg-white/10 ${
            pathname === "/add-destination" ? "bg-[#CCFF00]/15 text-[#CCFF00]" : "text-zinc-200"
          }`}
          data-testid="nav-add-destination"
        >
          <MapPinPlusIcon size={18} className="text-[#CCFF00]" /><span className="hidden lg:inline">{t("nav.addDestination")}</span>
        </Link>


        {user ? (
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-full bg-white/5 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-white/10" data-testid="user-menu-trigger">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#CCFF00] text-xs font-bold text-black">{(user.name || user.email || "R")[0].toUpperCase()}</span>
                <span className="hidden max-w-[100px] truncate md:inline">{user.name}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="z-[1300] w-56 border-white/10 bg-[#1c1c1f] text-white">
              <div className="px-2 py-1.5"><p className="truncate text-sm font-semibold">{user.name}</p><p className="truncate text-xs text-zinc-400">{user.email}</p></div>
              <DropdownMenuSeparator className="bg-white/10" />
              {isAdminUser(user) && (
                <DropdownMenuItem
                  className={`cursor-pointer font-semibold focus:bg-white/10 ${pathname.startsWith("/admin") ? "bg-[#CCFF00]/10 text-[#CCFF00] focus:text-[#CCFF00]" : "text-zinc-200"}`}
                  onClick={() => navigate("/admin")}
                  data-testid="menu-admin-console"
                >
                  <SlidersHorizontalIcon size={16} className="mr-2" /> {t("nav.adminConsole")}
                </DropdownMenuItem>
              )}
              {isAdminUser(user) && <DropdownMenuSeparator className="bg-white/10" />}
              <DropdownMenuItem
                className={`cursor-pointer focus:bg-white/10 ${pathname === "/profile" ? "bg-[#CCFF00]/10 text-[#CCFF00]" : ""}`}
                onClick={() => navigate("/profile")}
                data-testid="menu-rider-profile"
              >
                <UserIcon size={16} className="mr-2" /> {t("nav.riderProfile")}
              </DropdownMenuItem>
              <DropdownMenuItem
                className={`cursor-pointer focus:bg-white/10 ${pathname === "/my-map" ? "bg-[#CCFF00]/10 text-[#CCFF00]" : ""}`}
                onClick={() => navigate("/my-map")}
                data-testid="menu-personal-map"
              >
                <MapPinIcon size={16} className="mr-2" /> {t("nav.myMap")}
              </DropdownMenuItem>
                            <DropdownMenuItem className="cursor-pointer text-[#FF4500] focus:bg-white/10" onClick={async () => { await logout(); toast.success(t("nav.signOut")); navigate("/"); }} data-testid="logout-btn"><SignOutIcon size={16} className="mr-2" /> {t("nav.signOut")}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Link to="/login" className="flex items-center gap-1.5 rounded-full bg-[#CCFF00] px-4 py-1.5 text-sm font-bold text-black transition-transform active:scale-95" data-testid="nav-login"><UserIcon size={16} weight="bold" /> {t("nav.signIn")}</Link>
        )}
      </div>
    </header>
  );
}
